/**
 * NotConnectedYet — Tier 1 (entities) presentational placeholder for
 * admin surfaces whose FE shell is built but whose BE wiring
 * (controller endpoint, TanStack Query hook, DTO mapper) hasn't landed
 * yet.
 *
 * **Promotion history.** This primitive started at Tier 0, slice-local
 * to `modules/property/_components/` (SCRUM-167 scaffold). SCRUM-333
 * fired the kit-promotion ladder's second-consumer trigger (ADR
 * `frontend-0004`): the `booking` slice became the second admin
 * consumer of the same "FE shell ahead of BE" placeholder, so the
 * component was promoted to Tier 1 (entities) and moved here in an
 * atomic step. Both `property` and `booking` now consume it via the
 * public barrel. Tier 1 sits below the kit (Tier 3): this is an
 * admin-presentational noun, not a cross-portal primitive — a third
 * portal needing it would fire the Tier 1 → Tier 3 trigger.
 *
 * **Why a distinct primitive instead of reusing `AdminComingSoonPanel`
 * or the identity slice's `ComingSoonPanel`?**
 *
 * The two existing placeholders both say "Coming soon" — i.e. "we
 * haven't started this surface." `NotConnectedYet` says something
 * different and more honest: the shell IS built — the user can already
 * see the page chrome, the heading, the layout — but the data isn't
 * wired yet. Per the standing rule from Miguel ("every shell uses the
 * same `NotConnectedYet` primitive — honest 'Not connected yet'
 * caption, never fake data") this distinction is load-bearing.
 * Confusing "Coming soon" with "Not connected yet" would invite a
 * future contributor to drop a `MOCK_PROPERTIES` array on the page;
 * the stricter copy is the guard rail.
 *
 * Accessibility — semantic region with a stable heading id, no color
 * as sole carrier (the icon + heading + body text + ticket cite all
 * convey the state independently), passes axe-core with zero
 * violations.
 */

import * as React from 'react';
import { Icon, type IconName } from '@sparta/ui-kit';

export interface NotConnectedYetProps {
  /**
   * Human label for the surface ("Properties", "New property", "Unit
   * detail"…). Rendered as the visible heading and as the section's
   * accessible name via `aria-labelledby`.
   */
  readonly surface: string;
  /**
   * Optional Jira key for the per-surface ticket that will replace
   * the placeholder ("SCRUM-169"…). Omit while a surface's build
   * ticket is still upstream so the panel doesn't cite a key that
   * doesn't exist yet.
   */
  readonly ticket?: string;
  /**
   * Optional sentinel icon. Defaults to `building` — the same glyph
   * the LeftMenu uses for the Properties slot, so the placeholder
   * reads as "this is the Properties surface, just unfinished" at a
   * glance. Override on non-property routes that consume this
   * primitive.
   */
  readonly icon?: IconName;
}

/** Stable id counter so multiple placeholders on the same page each
 *  get a unique heading id for their `aria-labelledby` wiring.
 *  React's `useId` would be cleaner, but we render in SSR-style
 *  tests (RTL) where `useId`'s hydration mismatches add noise — a
 *  monotonic counter is sufficient because the primitive only
 *  exposes a heading link, not a full form.
 */
let nextHeadingId = 0;

export function NotConnectedYet({
  surface,
  ticket,
  icon = 'building',
}: NotConnectedYetProps): React.ReactElement {
  // `useState(() => …)` initialises the id once per mount and pins
  // it for the component's lifetime — `useMemo` would recompute on
  // strict-mode double-render.
  const [headingId] = React.useState(
    () => `not-connected-heading-${nextHeadingId++}`,
  );

  return (
    <section
      aria-labelledby={headingId}
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <span
        aria-hidden="true"
        className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full"
        style={{
          backgroundColor: 'var(--bg-sunken)',
          color: 'var(--fg-tertiary)',
        }}
      >
        <Icon name={icon} size={24} />
      </span>
      <h1
        id={headingId}
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        {surface}
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Not connected yet.
      </p>
      {ticket !== undefined ? (
        <p
          className="mt-1 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Tracked under{' '}
          <span className="font-mono" style={{ color: 'var(--fg-primary)' }}>
            {ticket}
          </span>
          .
        </p>
      ) : null}
    </section>
  );
}
